import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import service from "../../../../services/service";
import { useParameters } from "../../../../context/parametersContext";
import { safeFormat, toWholeNumber } from "../../../../utils/financialHelper";
import "./moreReits.less";

const reitImages: Record<string, string> = {
  Embassy: new URL("../../../../assets/Embassy_1.png", import.meta.url).href,
  Mindspace: new URL("../../../../assets/Mindspace_4.png", import.meta.url)
    .href,
  Brookfield: new URL("../../../../assets/Brookfield_2.png", import.meta.url)
    .href,
  Nexus: new URL("../../../../assets/Nexus_2.png", import.meta.url).href,
};
const reitcard1 = new URL("../../../../assets/reitCard1.png", import.meta.url)
  .href;
const reitcard2 = new URL("../../../../assets/reitCard2.png", import.meta.url)
  .href;
const compareArrow = new URL(
  "../../../../assets/compare-arrow.png",
  import.meta.url
).href;

interface MoreReitsProps {
  currentReitId: string;
  limit?: number;
}

const MoreReits: React.FC<MoreReitsProps> = ({ currentReitId, limit }) => {
  const navigate = useNavigate();

  const handleCompareClick = (reitId?: string) => {
    if (reitId) {
      navigate("/reits/compare-properties", { state: { reitId } });
    } else {
      navigate("/reits/compare-properties");
    }
  };

  const handleArrowClick = () => {
    const idsArray = reitSummary.map((reit) => reit.id);
    navigate("/reits/compare", { state: { idsArray } });
  };

  const { reitSummary, setReitSummary, isLoading, setIsLoading } =
    useParameters();
  const [otherReits, setOtherReits] = useState<any[]>([]);
  const [expandedReits, setExpandedReits] = useState<{
    [key: string]: boolean;
  }>({});

  const toggleExpand = (id: string) => {
    setExpandedReits((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    const fetchReits = async () => {
      setIsLoading(true);
      try {
        const data = await service.getReits();
        setReitSummary(data);
      } catch (error) {
        console.error("Failed to load REITs:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (reitSummary.length === 0) {
      fetchReits();
    }
  }, [reitSummary.length, setReitSummary, setIsLoading]);

  useEffect(() => {
    if (reitSummary.length > 0) {
      const filtered = reitSummary.filter((reit) => reit.id !== currentReitId);
      setOtherReits(limit ? filtered.slice(0, limit) : filtered);
    }
  }, [reitSummary, currentReitId, limit]);

  if (isLoading) {
    return <p className="loading-text">Loading More REITs...</p>;
  }

  return (
    <section className="more-reits-section">
      <div className="more-reits">
        {/* Compare Section */}
        <div className="compare-section">
          <div className="card">
            <img src={reitcard2} alt="Compare REITs" className="card-img" />
            <h3 className="card-title">Compare REITs</h3>
            <p className="card-desc">
              Compare financial metrics to make smarter choice
            </p>
            <button
              className="card-btn"
              onClick={() => {
                handleArrowClick();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
          </div>

          <div className="card">
            <img
              src={reitcard1}
              alt="Compare REIT Properties"
              className="card-img"
            />
            <h3 className="card-title">Compare REITs Properties</h3>
            <p className="card-desc">
              Uncover property strength across locations and scale
            </p>
            <button
              className="card-btn"
              onClick={() => {
                handleCompareClick();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
          </div>
        </div>

        {/* More REITs Section */}
        <h3 className="more-reits__title">More REITs</h3>

        <div className="reit-cards">
          {otherReits.map((reit, index) => {
            const isExpanded = expandedReits[reit.id];
            const visibleLocations = isExpanded
              ? reit.location
              : reit.location.slice(0, 5);

            return (
              <div key={reit.id} className="reit-card">
                {/* Left image */}
                <img
                  src={reitImages[reit.name.split(" ")[0]]}
                  alt={reit.name}
                  className="reit-banner"
                />

                {/* Right content */}
                <div className="reit-content">
                  <div className="reit-header">
                    <div className="reit-header__title-block">
                      <h3 className="reit-header__title">{reit.name}</h3>
                    </div>
                    <div className="reit-header__actions">
                      <p className="reit-header__listing-date">
                        Listing on:{" "}
                        {new Date(reit.date).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </p>
                      <button
                        className="reit-header__compare-btn"
                        onClick={() => {
                          handleCompareClick(reit.id);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        <span className="reit-header__compare-btn-text">
                          Compare
                        </span>
                        <span className="reit-header__compare-btn-icon">
                          <img src={compareArrow} alt="compareArrow" />
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Locations with expand/collapse */}
                  <div className="reit-locations">
                    {(isExpanded
                      ? reit.location
                      : reit.location.slice(0, 5)
                    ).map((city: string, idx: number, arr: string[]) => (
                      <span key={city}>
                        {city}
                        {idx < arr.length - 1 ? ", " : ""}
                        {idx === arr.length - 1 && reit.location.length > 5 && (
                          <span
                            className="more-toggle"
                            onClick={() => toggleExpand(reit.id)}
                          >
                            {isExpanded ? "–" : "+"}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>

                  {/* Stats row + actions */}
                  <div className="reit-stats-row">
                    <div className="stat-block">
                      <span>
                        BSE <span className="tag">Prev Close</span>
                      </span>
                      <p>
                        {safeFormat(reit.exchanges[0]?.openPrice)}{" "}
                        <span
                          className={`change ${
                            Number(reit.exchanges[0]?.openPriceChange) >= 0
                              ? "change--positive"
                              : "change--negative"
                          }`}
                        >
                          (
                          {Number(reit.exchanges[0]?.openPriceChange) > 0
                            ? `+${safeFormat(
                                Number(reit.exchanges[0]?.openPriceChange)
                              )}`
                            : safeFormat(
                                Number(reit.exchanges[0]?.openPriceChange)
                              )}
                          )
                        </span>
                      </p>
                    </div>

                    <div className="stat-block">
                      <span>
                        NSE <span className="tag">Prev Close</span>
                      </span>
                      <p>
                        {safeFormat(reit.exchanges[1]?.openPrice)}{" "}
                        <span
                          className={`change ${
                            Number(reit.exchanges[1]?.openPriceChange) >= 0
                              ? "change--positive"
                              : "change--negative"
                          }`}
                        >
                          (
                          {Number(reit.exchanges[1]?.openPriceChange) > 0
                            ? `+${safeFormat(
                                Number(reit.exchanges[1]?.openPriceChange)
                              )}`
                            : safeFormat(
                                Number(reit.exchanges[1]?.openPriceChange)
                              )}
                          )
                        </span>
                      </p>
                    </div>
                    <div className="stat-block">
                      <span>{reit.portfolio.key}</span>
                      <p className="portfolio-value">
                        {typeof reit.portfolio.value === "number"
                          ? `${safeFormat(reit.portfolio.value)}`
                          : reit.portfolio.value}
                      </p>
                    </div>
                    <div className="stat-block">
                      <span>{reit.marketcap.key} (Approx)</span>
                      <p>{toWholeNumber(reit.marketcap.value)}</p>
                    </div>

                    <div className="reit-actions">
                      <NavLink
                        to={`/reits/${reit.id}`}
                        onClick={() =>
                          window.scrollTo({ top: 0, behavior: "smooth" })
                        }
                      >
                        <button className="btn-secondary">Know More</button>
                      </NavLink>
                      <NavLink
                        to={`/reits/${reit.id}/valuation`}
                        onClick={() =>
                          window.scrollTo({ top: 0, behavior: "smooth" })
                        }
                      >
                        <button className="btn-primary">Valuation</button>
                      </NavLink>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MoreReits;
